import { FormEvent, useMemo, useState, useTransition } from "react";
import {
  createQuiz,
  deleteQuiz,
  updateQuiz,
} from "@/lib/api";
import type { Quiz } from "@/types/api";

type ManageQuizzesProps = {
  initialQuizzes: Quiz[];
};

type QuestionForm = {
  id?: string | null;
  text_en: string;
  text_hi?: string | null;
  text_ru?: string | null;
  cooldown: number;
  time: number;
  marks: number;
  image?: string | null;
  options: Array<{
    id?: string | null;
    text_en: string;
    text_hi?: string | null;
    text_ru?: string | null;
    is_correct: boolean;
  }>;
};

type QuizFormState = {
  name: string;
  description?: string;
  questions: QuestionForm[];
};

const emptyOption = (isCorrect = false) => ({
  id: undefined,
  text_en: "",
  text_hi: "",
  text_ru: "",
  is_correct: isCorrect,
});

const emptyQuestion = (): QuestionForm => ({
  id: undefined,
  text_en: "",
  text_hi: "",
  text_ru: "",
  cooldown: 5,
  time: 30,
  marks: 10,
  image: "",
  options: [emptyOption(true), emptyOption()],
});

const emptyForm: QuizFormState = {
  name: "",
  description: "",
  questions: [emptyQuestion()],
};

function mapQuizToForm(quiz: Quiz): QuizFormState {
  return {
    name: quiz.name,
    description: quiz.description ?? "",
    questions: quiz.questions.map((question) => ({
      id: question.id ?? undefined,
      text_en: question.text.en,
      text_hi: question.text.hi ?? "",
      text_ru: question.text.ru ?? "",
      cooldown: question.cooldown,
      time: question.time,
      marks: question.marks,
      image: question.image ?? "",
      options: question.options.map((option) => ({
        id: option.id ?? undefined,
        text_en: option.text.en,
        text_hi: option.text.hi ?? "",
        text_ru: option.text.ru ?? "",
        is_correct: Boolean(option.is_correct),
      })),
    })),
  };
}

function mapFormToPayload(form: QuizFormState): QuizFormState {
  return {
    ...form,
    questions: form.questions.map((question) => ({
      ...question,
      options: question.options.map((option) => ({
        ...option,
        is_correct: Boolean(option.is_correct),
      })),
    })),
  };
}

export function ManageQuizzes({ initialQuizzes }: ManageQuizzesProps) {
  const [quizzes, setQuizzes] = useState(initialQuizzes);
  const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);
  const [form, setForm] = useState<QuizFormState>(emptyForm);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [isPending, startTransition] = useTransition();

  const selectedQuiz = useMemo(
    () => quizzes.find((q) => q.id === selectedQuizId),
    [quizzes, selectedQuizId],
  );

  const resetForm = () => {
    setSelectedQuizId(null);
    setForm(emptyForm);
  };

  const showMessage = (tone: "success" | "error", text: string) => {
    setMessageTone(tone);
    setMessage(text);
    setTimeout(() => setMessage(null), 5000);
  };

  const handleSelectQuiz = (quizId: string) => {
    const quiz = quizzes.find((item) => item.id === quizId);
    if (!quiz) return;
    setSelectedQuizId(quizId);
    setForm(mapQuizToForm(quiz));
  };

  const handleFormChange = <K extends keyof QuizFormState>(
    key: K,
    value: QuizFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleQuestionChange = <K extends keyof QuestionForm>(
    index: number,
    key: K,
    value: QuestionForm[K],
  ) => {
    setForm((prev) => {
      const updated = [...prev.questions];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, questions: updated };
    });
  };

  const handleOptionChange = (
    questionIndex: number,
    optionIndex: number,
    key: keyof QuestionForm["options"][number],
    value: QuestionForm["options"][number][typeof key],
  ) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const options = [...questions[questionIndex].options];
      options[optionIndex] = { ...options[optionIndex], [key]: value };
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...prev, questions };
    });
  };

  const markCorrectOption = (questionIndex: number, optionIndex: number) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const options = questions[questionIndex].options.map((option, index) => ({
        ...option,
        is_correct: index === optionIndex,
      }));
      questions[questionIndex] = { ...questions[questionIndex], options };
      return { ...prev, questions };
    });
  };

  const addQuestion = () => {
    setForm((prev) => ({
      ...prev,
      questions: [...prev.questions, emptyQuestion()],
    }));
  };

  const removeQuestion = (index: number) => {
    setForm((prev) => {
      const updated = prev.questions.filter((_, i) => i !== index);
      return { ...prev, questions: updated.length ? updated : [emptyQuestion()] };
    });
  };

  const addOption = (questionIndex: number) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const hasCorrect = questions[questionIndex].options.some(
        (option) => option.is_correct,
      );
      const nextOptions = [
        ...questions[questionIndex].options,
        emptyOption(!hasCorrect),
      ];
      questions[questionIndex] = { ...questions[questionIndex], options: nextOptions };
      return { ...prev, questions };
    });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    setForm((prev) => {
      const questions = [...prev.questions];
      const options = questions[questionIndex].options.filter(
        (_, index) => index !== optionIndex,
      );
      const nextOptions = options.length ? options : [emptyOption(true)];
      if (!nextOptions.some((option) => option.is_correct)) {
        nextOptions[0] = { ...nextOptions[0], is_correct: true };
      }
      questions[questionIndex] = {
        ...questions[questionIndex],
        options: nextOptions,
      };
      return { ...prev, questions };
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const payload = mapFormToPayload(form);

    if (!payload.name.trim()) {
      showMessage("error", "Name is required to save a quiz.");
      return;
    }

    const hasCorrectOption = payload.questions.every((question) =>
      question.options.some((option) => option.is_correct),
    );

    if (!hasCorrectOption) {
      showMessage("error", "Each question must have one correct option selected.");
      return;
    }

    if (payload.questions.length === 0) {
      showMessage("error", "Include at least one question.");
      return;
    }

    const normalizedQuestions = payload.questions.map((question, questionIndex) => {
      const primaryPrompt =
        question.text_en.trim() ||
        question.text_hi?.trim() ||
        question.text_ru?.trim();

      if (!primaryPrompt) {
        throw new Error(
          `Question ${questionIndex + 1} requires text in at least one language.`,
        );
      }

      const options = question.options.map((option, optionIndex) => {
        const primaryOptionText =
          option.text_en.trim() ||
          option.text_hi?.trim() ||
          option.text_ru?.trim();

        if (!primaryOptionText) {
          throw new Error(
            `Question ${questionIndex + 1}, option ${optionIndex + 1} requires text in at least one language.`,
          );
        }

        return {
          id: option.id,
          text: {
            en: primaryOptionText,
            hi: option.text_hi?.trim() || undefined,
            ru: option.text_ru?.trim() || undefined,
          },
          is_correct: option.is_correct,
        };
      });

      return {
        id: question.id,
        text: {
          en: primaryPrompt,
          hi: question.text_hi?.trim() || undefined,
          ru: question.text_ru?.trim() || undefined,
        },
        cooldown: Number(question.cooldown),
        time: Number(question.time),
        marks: Number(question.marks),
        image: question.image?.trim() || undefined,
        options,
        correct_option_id:
          question.options.find((option) => option.is_correct)?.id ?? null,
      };
    });

    startTransition(async () => {
      try {
        const submitPayload = {
          name: payload.name,
          description: payload.description?.trim(),
          questions: normalizedQuestions,
        };

        const result = selectedQuizId
          ? await updateQuiz(selectedQuizId, submitPayload)
          : await createQuiz(submitPayload);

        if (!result) {
          showMessage(
            "error",
            "Unable to persist quiz. Validate fields and try again.",
          );
          return;
        }

        if (selectedQuizId) {
          setQuizzes((prev) =>
            prev.map((quiz) => (quiz.id === selectedQuizId ? result : quiz)),
          );
          showMessage("success", "Quiz updated successfully.");
        } else {
          setQuizzes((prev) => [result, ...prev]);
          showMessage("success", "Quiz created successfully.");
          resetForm();
        }
      } catch (err) {
        if (err instanceof Error) {
          showMessage("error", err.message);
        } else {
          showMessage("error", "Unable to persist quiz. Check field entries.");
        }
      }
    });
  };

  const handleDelete = () => {
    if (!selectedQuizId) return;
    startTransition(async () => {
      await deleteQuiz(selectedQuizId);
      setQuizzes((prev) => prev.filter((quiz) => quiz.id !== selectedQuizId));
      showMessage("success", "Quiz removed from library.");
      resetForm();
    });
  };

  return (
    <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            Quiz workspace
          </h3>
          <p className="text-sm text-neutral-500">
            Select an existing quiz to update or draft a new template.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={resetForm}
            className="rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
          >
            New quiz
          </button>
          {selectedQuizId ? (
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>

      {quizzes.length ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {quizzes.map((quiz) => (
            <button
              type="button"
              key={quiz.id}
              onClick={() => handleSelectQuiz(quiz.id)}
              className={`rounded-full border px-3 py-1 text-sm ${
                selectedQuizId === quiz.id
                  ? "border-neutral-900 bg-neutral-900 text-white"
                  : "border-neutral-200 text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {quiz.name}
            </button>
          ))}
        </div>
      ) : null}

      {message ? (
        <div
          className={`mt-4 rounded-md border px-4 py-2 text-sm ${
            messageTone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-600"
          }`}
        >
          {message}
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-neutral-700">Quiz name</span>
            <input
              type="text"
              value={form.name}
              onChange={(event) => handleFormChange("name", event.target.value)}
              className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              placeholder="Incident response fundamentals"
              required
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-neutral-700">Description</span>
            <input
              type="text"
              value={form.description}
              onChange={(event) =>
                handleFormChange("description", event.target.value)
              }
              className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              placeholder="Optional context for facilitators"
            />
          </label>
        </div>

        <div className="space-y-8">
          {form.questions.map((question, questionIndex) => (
            <div
              key={`question-${questionIndex}`}
              className="rounded-lg border border-neutral-200 bg-neutral-50 p-4"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-neutral-800">
                  Question {questionIndex + 1}
                </h4>
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <button
                    type="button"
                    onClick={() => removeQuestion(questionIndex)}
                    className="rounded-md border border-neutral-200 px-2 py-1 hover:bg-neutral-100"
                  >
                    Remove
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                  <span className="font-medium text-neutral-700">
                    Prompt (English)
                  </span>
                  <textarea
                    value={question.text_en}
                    onChange={(event) =>
                      handleQuestionChange(
                        questionIndex,
                        "text_en",
                        event.target.value,
                      )
                    }
                    className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                    rows={3}
                    placeholder="What is the first step when triaging a potential breach?"
                  />
                </label>
                <details className="rounded-md border border-neutral-200 bg-white/70 px-3 py-2 text-sm text-neutral-600 sm:col-span-2">
                  <summary className="cursor-pointer text-xs font-semibold text-neutral-700">
                    Localized prompts
                  </summary>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium text-neutral-600">Hindi</span>
                      <textarea
                        value={question.text_hi ?? ""}
                        onChange={(event) =>
                          handleQuestionChange(
                            questionIndex,
                            "text_hi",
                            event.target.value,
                          )
                        }
                        className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                        rows={2}
                        placeholder="हिंदी अनुवाद"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium text-neutral-600">Russian</span>
                      <textarea
                        value={question.text_ru ?? ""}
                        onChange={(event) =>
                          handleQuestionChange(
                            questionIndex,
                            "text_ru",
                            event.target.value,
                          )
                        }
                        className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                        rows={2}
                        placeholder="Русский перевод"
                      />
                    </label>
                  </div>
                </details>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-neutral-700">
                      Cooldown (sec)
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={question.cooldown}
                      onChange={(event) =>
                        handleQuestionChange(
                          questionIndex,
                          "cooldown",
                          Number(event.target.value),
                        )
                      }
                      className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-neutral-700">
                      Time limit (sec)
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={question.time}
                      onChange={(event) =>
                        handleQuestionChange(
                          questionIndex,
                          "time",
                          Number(event.target.value),
                        )
                      }
                      className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-neutral-700">
                      Marks
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={question.marks}
                      onChange={(event) =>
                        handleQuestionChange(
                          questionIndex,
                          "marks",
                          Number(event.target.value),
                        )
                      }
                      className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-neutral-700">
                      Image URL
                    </span>
                    <input
                      type="url"
                      value={question.image ?? ""}
                      onChange={(event) =>
                        handleQuestionChange(
                          questionIndex,
                          "image",
                          event.target.value,
                        )
                      }
                      className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                      placeholder="https://..."
                    />
                  </label>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <h5 className="text-sm font-semibold text-neutral-700">
                  Answer options
                </h5>
                {question.options.map((option, optionIndex) => (
                  <div
                    key={`option-${questionIndex}-${optionIndex}`}
                    className="rounded-md border border-neutral-200 bg-white p-3"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <label className="flex flex-1 flex-col gap-1 text-sm">
                        <span className="font-medium text-neutral-700">
                          Option text (English)
                        </span>
                        <input
                          type="text"
                          value={option.text_en}
                          onChange={(event) =>
                            handleOptionChange(
                              questionIndex,
                              optionIndex,
                              "text_en",
                              event.target.value,
                            )
                          }
                          className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                          placeholder="Validate incident scope"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-700">
                        <input
                          type="radio"
                          name={`question-${questionIndex}-correct`}
                          checked={option.is_correct}
                          onChange={() => markCorrectOption(questionIndex, optionIndex)}
                          className="h-4 w-4 border-neutral-300"
                        />
                        Correct answer
                      </label>
                    </div>
                    <details className="mt-3 rounded-md border border-dashed border-neutral-200 bg-neutral-50/60 px-3 py-2 text-xs text-neutral-600">
                      <summary className="cursor-pointer font-semibold text-neutral-700">
                        Localized option text
                      </summary>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        <label className="flex flex-col gap-1 text-xs">
                          <span className="font-medium text-neutral-600">Hindi</span>
                          <input
                            type="text"
                            value={option.text_hi ?? ""}
                            onChange={(event) =>
                              handleOptionChange(
                                questionIndex,
                                optionIndex,
                                "text_hi",
                                event.target.value,
                              )
                            }
                            className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                            placeholder="हिंदी अनुवाद"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-xs">
                          <span className="font-medium text-neutral-600">
                            Russian
                          </span>
                          <input
                            type="text"
                            value={option.text_ru ?? ""}
                            onChange={(event) =>
                              handleOptionChange(
                                questionIndex,
                                optionIndex,
                                "text_ru",
                                event.target.value,
                              )
                            }
                            className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                            placeholder="Русский перевод"
                          />
                        </label>
                      </div>
                    </details>
                    <button
                      type="button"
                      onClick={() => removeOption(questionIndex, optionIndex)}
                      className="mt-3 text-xs font-medium text-neutral-500 hover:text-neutral-800"
                    >
                      Remove option
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addOption(questionIndex)}
                  className="inline-flex items-center rounded-md border border-neutral-200 px-3 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
                >
                  Add option
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={addQuestion}
            className="rounded-md border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100"
          >
            Add question
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="inline-flex items-center justify-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-70"
          >
            {isPending
              ? selectedQuiz
                ? "Saving..."
                : "Creating..."
              : selectedQuiz
              ? "Save changes"
              : "Create quiz"}
          </button>
        </div>
      </form>
    </section>
  );
}


